import {
  Box,
  FormControl,
  MenuItem,
  OutlinedInput,
  Select,
} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { useTranslation } from "react-i18next"
import { ChevronNextIcon, ChevronPrevIcon } from "../Icons/LivisIcons"
import "./Pagination.scss"

export const DEFAULT_PAGINATION_FREQUENCY_OPTIONS = [
  "10",
  "20",
  "50",
  "100",
  "200",
]

export interface LivisPaginationFrequency {
  value: string
  onChange: (value: string) => void
  options?: string[]
}

export interface LivisPaginationProps {
  currentPage?: number
  pageLength?: number
  totalCount?: number
  startCount?: number
  onPrev?: () => void
  onNext?: () => void
  frequency?: LivisPaginationFrequency
  className?: string
}

export const LivisPagination = ({
  currentPage = 1,
  pageLength = 0,
  totalCount = 0,
  startCount,
  onPrev,
  onNext,
  frequency,
  className = "",
}: LivisPaginationProps) => {
  const { t } = useTranslation()

  const showPagination = !!onPrev && !!onNext
  const total = totalCount || 0
  const computedStart =
    startCount !== undefined
      ? startCount
      : total > 0
      ? (currentPage - 1) * pageLength + 1
      : 0
  const start = total > 0 ? computedStart : 0
  const end = total > 0 ? Math.min(start + pageLength - 1, total) : 0

  const isFirstPage = currentPage <= 1 || total === 0
  const isLastPage = end >= total || total === 0

  const freqOptions = frequency?.options || DEFAULT_PAGINATION_FREQUENCY_OPTIONS

  return (
    <Box
      className={`livis-pagination livis-manage-user-pagination-wrap ${className}`.trim()}
    >
      {frequency && (
        <div className="livis-pagination__frequency livis-operator-pagination-frequency">
          <FormControl
            fullWidth
            size="small"
            className="livis-pagination__select livis-frequency-select-form"
            id="livis-pagination-control-filter"
          >
            <Select
              labelId="livis-pagination-frequency-label"
              id="livis-pagination-frequency-id"
              value={frequency.value}
              label={frequency.value}
              input={<OutlinedInput />}
              onChange={(e) => frequency.onChange(e.target.value as string)}
              IconComponent={(props) => <ExpandMoreIcon {...props} />}
            >
              {freqOptions.map((option) => (
                <MenuItem
                  className="livis-menu-item"
                  key={option}
                  value={option}
                >
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      )}

      {showPagination && (
        <div
          className="livis-pagination__nav livis-inspection-pagination-button-wrap"
          id="livis-station-display-frequency"
        >
          <p>
            <b>
              {start}-{end}
            </b>{" "}
            {t("of")} {total}
          </p>
          <a
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !isFirstPage) onPrev?.()
            }}
            style={{
              pointerEvents: isFirstPage ? "none" : "auto",
              opacity: isFirstPage ? 0.5 : 1,
              cursor: isFirstPage ? "not-allowed" : "pointer",
            }}
          >
            <ChevronPrevIcon onClick={() => !isFirstPage && onPrev?.()} />
          </a>
          <a
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !isLastPage) onNext?.()
            }}
            style={{
              pointerEvents: isLastPage ? "none" : "auto",
              opacity: isLastPage ? 0.5 : 1,
              cursor: isLastPage ? "not-allowed" : "pointer",
            }}
          >
            <ChevronNextIcon onClick={() => !isLastPage && onNext?.()} />
          </a>
        </div>
      )}
    </Box>
  )
}
